import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Badge,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import AuthContext from "../contexts/AuthContext";
import { toast, Toaster } from "react-hot-toast";
import api from "../api";

const MyWorksheets = () => {
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSavedId, setRecentSavedId] = useState(null);
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch saved worksheets when component mounts or when returning from save
  useEffect(() => {
    console.log("MyWorksheets component mounted");
    console.log("Current authenticated user:", auth.user);
    console.log("Current location state:", location.state);

    // Check if we came from saving a worksheet
    if (location.state?.fromSave) {
      setRecentSavedId(location.state.savedWorksheetId);
      toast.success(
        "Worksheet saved successfully! It should appear in your list."
      );
    }

    if (auth.loading) {
      console.log("Auth is still loading, waiting...");
      return;
    }

    // Only fetch if user is authenticated
    if (auth.isAuthenticated && auth.user) {
      console.log("User is authenticated, fetching worksheets");

      // Force refresh user data to ensure we have the latest
      const refreshUserData = async () => {
        try {
          console.log("Refreshing user data before fetching worksheets");
          const userResponse = await api.get("/api/users/me/");
          console.log("Current user verification:", userResponse.data);
          fetchWorksheets();
        } catch (error) {
          console.error("Error refreshing user data:", error);
          setLoading(false);
          if (error.response?.status === 401) {
            toast.error("Your session has expired. Please log in again.");
            navigate("/login");
          }
        }
      };

      refreshUserData();

      // Set up a timer to refresh worksheets every few seconds while on this page
      const refreshTimer = setInterval(() => {
        console.log("Refreshing worksheets list...");
        fetchWorksheets(false); // Don't show loading spinner for background refreshes
      }, 3000);

      // Clean up the timer when component unmounts
      return () => clearInterval(refreshTimer);
    } else {
      console.log("No authenticated user found, not fetching worksheets");
      setLoading(false);
      toast.error("Please log in to view your worksheets");
      navigate("/login");
    }
  }, [
    auth.isAuthenticated,
    auth.loading,
    auth.user,
    location.state?.timestamp,
  ]);

  const fetchWorksheets = async (showLoadingIndicator = true) => {
    if (!auth.isAuthenticated || !auth.user) {
      console.log("Cannot fetch worksheets: No authenticated user");
      setWorksheets([]);
      setLoading(false);
      return;
    }

    if (showLoadingIndicator) {
      setLoading(true);
    }
    setError("");

    try {
      console.log("Fetching worksheets for user:", auth.user.username);

      // Now use our API helper
      const response = await api.get("/api/worksheets/");
      console.log("Raw API response:", response);

      // Extra check to ensure we've received valid data
      let worksheetData = [];
      if (response.data && Array.isArray(response.data)) {
        worksheetData = response.data;
        console.log("Setting worksheets array:", worksheetData);
      } else if (response.data && typeof response.data === "object") {
        // If it's an object but not an array, check if it contains results property (common DRF pattern)
        if (Array.isArray(response.data.results)) {
          worksheetData = response.data.results;
          console.log("Setting worksheets from results array:", worksheetData);
        } else {
          console.warn(
            "API returned an object without a results array:",
            response.data
          );
        }
      } else {
        console.warn(
          "API did not return an array for worksheets:",
          response.data
        );
      }

      // Set the worksheets data
      setWorksheets(worksheetData);

      // If we still don't see worksheets but server returned 200, show a message
      if (worksheetData.length === 0) {
        console.log("Server returned empty worksheets array");
        toast.error(
          "No worksheets found for your account. Try creating a new one."
        );
      }
    } catch (error) {
      console.error("Error fetching worksheets:", error);
      if (error.response) {
        console.error(
          "Error response:",
          error.response.status,
          error.response.data
        );

        // If unauthorized, try to refresh token or redirect to login
        if (error.response.status === 401 || error.response.status === 403) {
          toast.error("Your session has expired. Please log in again.");
          navigate("/login");
          return;
        }
      }
      setError("Failed to fetch saved worksheets. Please try again later.");
      setWorksheets([]);
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  const handleDeleteWorksheet = async () => {
    if (!selectedWorksheet) return;

    setIsDeleting(true);

    try {
      await api.delete(`/api/worksheets/${selectedWorksheet.id}/`);
      setShowDeleteModal(false);
      setSelectedWorksheet(null);
      toast.success("Worksheet deleted successfully");

      // Refresh worksheet list
      fetchWorksheets();
    } catch (error) {
      console.error("Error deleting worksheet:", error);
      setError("Failed to delete the worksheet. Please try again.");
      toast.error("Failed to delete worksheet");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLoadWorksheet = async (worksheet) => {
    setIsLoading(true);
    setError("");

    try {
      // Navigate to worksheet generator with worksheet ID in state
      navigate("/worksheets", { state: { worksheetId: worksheet.id } });
    } catch (error) {
      console.error("Error preparing to load worksheet:", error);
      setError("Failed to load the worksheet. Please try again.");
      setIsLoading(false);
    }
  };

  const handleTogglePublic = async (worksheet) => {
    if (!worksheet) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await api.post(
        `/api/worksheets/${worksheet.id}/toggle_public/`
      );
      console.log("Worksheet public status updated:", response.data);
      toast.success("Worksheet public status updated successfully");

      // Refresh worksheet list
      fetchWorksheets();
    } catch (error) {
      console.error("Error toggling worksheet public status:", error);
      setError("Failed to update worksheet public status. Please try again.");
      toast.error("Failed to update worksheet public status");
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Toaster position="top-right" />
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your worksheets...</p>
      </Container>
    );
  }

  // Make sure worksheets is an array before rendering
  const worksheetsArray = Array.isArray(worksheets) ? worksheets : [];

  // DEBUGGING
  console.log("Raw worksheets data:", worksheets);
  console.log("Converted to array:", worksheetsArray);
  console.log("Number of worksheets:", worksheetsArray.length);
  if (worksheetsArray.length > 0) {
    console.log("First worksheet:", worksheetsArray[0]);
  }

  return (
    <Container className="my-4">
      <Toaster position="top-right" />
      <Row className="mb-4">
        <Col>
          <h2>My Worksheets</h2>
          <p>
            View and manage your saved worksheets. You can view, edit, or delete
            your saved worksheets.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {worksheetsArray.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <Card.Title>No Saved Worksheets</Card.Title>
            <Card.Text>
              You haven't saved any worksheets yet. Go to the Worksheet
              Generator to create and save worksheets.
            </Card.Text>
            {/* 
            <Card.Text className="text-muted mt-3">
              <strong>Debug Info:</strong><br/>
              Current user: {auth.user?.username} (ID: {auth.user?.id})<br/>
              API responses: GET /api/worksheets/ returns {worksheets?.length || 0} items<br/>
              Raw data type: {typeof worksheets} {Array.isArray(worksheets) ? '(valid array)' : '(not array)'}
            </Card.Text>
            */}
            <Button
              variant="primary"
              onClick={() => navigate("/worksheets", { replace: true })}
            >
              Create Worksheet
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {worksheetsArray.map((worksheet) => (
            <Col md={6} lg={4} key={worksheet.id} className="mb-4">
              <Card
                className={
                  worksheet.id === recentSavedId ? "border-success" : ""
                }
                style={
                  worksheet.id === recentSavedId
                    ? { boxShadow: "0 0 8px rgba(40, 167, 69, 0.6)" }
                    : {}
                }
              >
                {worksheet.id === recentSavedId && (
                  <div className="bg-success text-white py-1 px-2 text-center">
                    <small>Just saved</small>
                  </div>
                )}
                <Card.Header className="d-flex justify-content-between align-items-start">
                  <h5 className="mb-0">{worksheet.title}</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <Badge bg="primary" className="me-2">
                      {worksheet.subject}
                    </Badge>
                    {worksheet.topic && (
                      <Badge bg="secondary" className="me-2">
                        {worksheet.topic}
                      </Badge>
                    )}
                    <Badge bg="info">{worksheet.difficulty}</Badge>
                  </div>

                  {worksheet.description && (
                    <Card.Text className="text-muted">
                      {worksheet.description}
                    </Card.Text>
                  )}

                  <div className="d-flex justify-content-between mt-3">
                    <small className="text-muted">
                      Created:{" "}
                      {new Date(worksheet.created_at).toLocaleDateString()}
                    </small>
                    <small className="text-muted">
                      {(() => {
                        try {
                          // Handle both string and array formats for backward compatibility
                          let codes;
                          if (typeof worksheet.question_codes === "string") {
                            codes = JSON.parse(worksheet.question_codes);
                          } else {
                            codes = worksheet.question_codes;
                          }
                          return Array.isArray(codes) ? codes.length : 0;
                        } catch (e) {
                          console.error(
                            "Error parsing question_codes:",
                            e,
                            worksheet.question_codes
                          );
                          return 0;
                        }
                      })()}{" "}
                      questions
                    </small>
                  </div>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-between">
                  <Button
                    variant="primary"
                    onClick={() => handleLoadWorksheet(worksheet)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Open"}
                  </Button>
                  <div>
                    <Button
                      variant={
                        worksheet.is_public
                          ? "outline-success"
                          : "outline-secondary"
                      }
                      onClick={() => handleTogglePublic(worksheet)}
                      className="me-2"
                      title={
                        worksheet.is_public ? "Make private" : "Share publicly"
                      }
                    >
                      {worksheet.is_public ? "Shared" : "Share"}
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={() => {
                        setSelectedWorksheet(worksheet);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Button
        variant="outline-primary"
        className="mt-3"
        onClick={() => {
          // Force a refresh of the worksheet list
          fetchWorksheets(true);
          toast.success("Refreshing worksheet list...");
        }}
      >
        Refresh Worksheet List
      </Button>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the worksheet "
          {selectedWorksheet?.title}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteWorksheet}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyWorksheets;
